import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import { AppDispatch, AppState } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import Input from "../../../components/input";
import Button from "../../../components/button";
import { AddOutlined } from "../../../components/icons/add-outlined";
import { MinusOutlined } from "../../../components/icons/minus-outlined";
import { Formik } from "formik";
import {
  clearCreateCourseState,
  createCourseStateAction,
  getCreateCourseState,
  CourseCreateRequest,
  CourseStatus,
  CourseLanguage,
  CourseLevel,
} from "../../../redux/features/course-slice";
import { UserRole } from "../../../redux/features/user-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  allIncidentUsersStateAction,
  getAllIncidentUsersState,
} from "../../../redux/features/incident-slice"; 
import paths from "../../../navigation/paths";
import { adminCat } from "../../../core/utils/constants";
import { isSelectSingleValue, isSelectMultiValue } from "../../../core/utils";
import { addWeeks, format } from "date-fns";
import DatePicker from "../../../components/date-picker";
import TimePicker from "../../../components/time-picker";
import InputFormat from "../../../components/input-format";

export default function AddCoursePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const createCourseState = useSelector(getCreateCourseState);
  const errorState = useSelector(getErrorState);
  const usersState = useSelector(getAllIncidentUsersState);
  const [newScheduleDate, setNewScheduleDate] = useState<Date | null>(null);
  const [newScheduleTime, setNewScheduleTime] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const submitCreateCourse = async (values: CourseCreateRequest) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("description", values.description);
    formData.append("syllabus", values.syllabus);
    if (values.prerequisite) formData.append("prerequisite", values.prerequisite);
    formData.append("duration", values.duration.toString());
    values.schedule.forEach((date, index) => {
      formData.append(`schedule[${index}]`, date);
    });
    formData.append("capacity", values.capacity.toString());
    formData.append("status", values.status);
    formData.append("language", values.language);
    formData.append("level", values.level);
    values.authorIds.forEach((id, index) => {
      formData.append(`authorIds[${index}]`, id);
    });
    if (selectedFile) {
      formData.append("picture", selectedFile);
    }

    await dispatch(
      createCourseStateAction({ request: formData, userId: "current-user-id" })
    );
  };

  const fetchAllUsers = useCallback(async () => {
    await dispatch(allIncidentUsersStateAction({ teamIds: [], roles: [] }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: paths.COURSES, path: null, title: "Courses" },
        { id: "addCourse", path: null, title: "Add Course" },
      ])
    );
    fetchAllUsers(); 
  }, [dispatch, fetchAllUsers]);

  useEffect(() => {
    if (createCourseState.data && createCourseState.data.success) {
      handleSuccessToast(
        createCourseState.data.message,
        clearCreateCourseState
      );
      setTimeout(() => {
        navigate(paths.COURSES, { replace: true });
      }, 2000);
    }
  }, [createCourseState, handleSuccessToast, navigate]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState, navigate]);

  const getDefaultScheduleDate = (schedule: string[]): Date => {
    if (schedule.length === 0) return addWeeks(new Date(), 1);
    const lastDate = new Date(schedule[schedule.length - 1]);
    return addWeeks(lastDate, 1);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      setSelectedFile(file);
    } else {
      toast("Please select a valid JPEG or PNG image", { type: "error" });
      setSelectedFile(null);
    }
  };

  return (
    <PermissionGate
      categories={[]}
      roles={[UserRole.Admin, UserRole.Trainer]}
    >
      <Helmet title="Add Course" />
      <Formik
        initialValues={
          {
            name: "",
            description: "",
            prerequisite: "",
            syllabus: "",
            duration: 0,
            schedule: [],
            capacity: 10,
            status: CourseStatus.Draft,
            language: CourseLanguage.Vietnamese,
            level: CourseLevel.Beginner,
            authorIds: [],
          } as CourseCreateRequest
        }
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Name is required").max(100),
          description: Yup.string().required("Description is required"),
          syllabus: Yup.string().required("Syllabus is required"),
          duration: Yup.number().required("Duration is required").min(1),
          schedule: Yup.array()
            .of(Yup.string().required())
            .min(1, "At least one schedule date is required"),
          capacity: Yup.number().required("Capacity is required").min(1),
          status: Yup.string().required("Status is required"),
          language: Yup.string().required("Language is required"),
          level: Yup.string().required("Level is required"),
          authorIds: Yup.array()
            .of(Yup.string().required())
            .min(1, "At least one author is required"),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          await submitCreateCourse(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
          dirty,
        }) => (
          <form
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            className="h-full overflow-y-auto"
            encType="multipart/form-data"
          >
            <div
              className="h-full grid grid-cols-1 sm:grid-cols-2 gap-8 overflow-y-scroll"
              style={{
                height: "calc(100% - 56px)",
              }}
            >
              <div className="space-y-4">
                <Input
                  label="Name *"
                  type="text"
                  id="name"
                  value={values.name}
                  onChange={handleChange}
                  errorText={errors.name}
                />
               <InputFormat
                  label="Description *"
                  id="description-editor"
                  height="100px"
                  value={values.description}
                  onChange={(value) => setValues({ ...values, description: value })}
                  errorText={errors.description}
                />
                <InputFormat
                  label="Syllabus *"
                  id="syllabus-editor"
                  value={values.syllabus}
                  onChange={(value) => setValues({ ...values, syllabus: value })}
                  errorText={errors.syllabus}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">Picture</label>
                  <input
                    type="file"
                    id="picture"
                    accept="image/jpeg,image/png"
                    onChange={handleFileChange}
                    className="border border-divider rounded-lg p-2"
                  />
                  {selectedFile && (
                    <p className="text-sm text-text-title">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
                <Input
                  label="Prerequisite"
                  type="text"
                  id="prerequisite"
                  value={values.prerequisite}
                  onChange={handleChange}
                  errorText={errors.prerequisite}
                />
                <Input
                  label="Duration (hours) *"
                  type="number"
                  id="duration"
                  value={values.duration.toString()}
                  onChange={handleChange}
                  errorText={errors.duration}
                />
                <Input
                  label="Capacity *"
                  type="number"
                  id="capacity"
                  value={values.capacity.toString()}
                  onChange={handleChange}
                  errorText={errors.capacity}
                />
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">Status *</label>
                  <StyledSelect
                    options={Object.values(CourseStatus).map((status) => ({
                      value: status,
                      label: status.charAt(0).toUpperCase() + status.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.status,
                      label:
                        values.status.charAt(0).toUpperCase() +
                        values.status.slice(1),
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          status: value.value as CourseStatus,
                        });
                      }
                    }}
                  />
                  {errors.status && (
                    <p className="text-error-main text-sm">{errors.status}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">Language *</label>
                  <StyledSelect
                    options={Object.values(CourseLanguage).map((language) => ({
                      value: language,
                      label:
                        language.charAt(0).toUpperCase() + language.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.language,
                      label:
                        values.language.charAt(0).toUpperCase() +
                        values.language.slice(1),
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          language: value.value as CourseLanguage,
                        });
                      }
                    }}
                  />
                  {errors.language && (
                    <p className="text-error-main text-sm">{errors.language}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-sm text-text-title">Level *</label>
                  <StyledSelect
                    options={Object.values(CourseLevel).map((level) => ({
                      value: level,
                      label: level.charAt(0).toUpperCase() + level.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={{
                      value: values.level,
                      label:
                        values.level.charAt(0).toUpperCase() +
                        values.level.slice(1),
                    }}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          level: value.value as CourseLevel,
                        });
                      }
                    }}
                  />
                  {errors.level && (
                    <p className="text-error-main text-sm">{errors.level}</p>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                  <label className="text-sm text-text-title">Authors *</label>
                  <StyledSelect
                    multiple
                    isCloseMenuOnSelect={false}
                    loading={usersState.loading}
                    options={usersState.data.map((user) => ({
                      value: user.id,
                      label: `${user.name} - ${user.email}`,
                    }))}
                    menuPosition="fixed"
                    value={
                      values.authorIds
                        ? values.authorIds
                            .map((authorId) => {
                              const user = usersState.data.find(
                                (u) => u.id === authorId
                              );
                              return user
                                ? {
                                    value: user.id,
                                    label: `${user.name} - ${user.email}`,
                                  }
                                : null;
                            })
                            .filter(Boolean) as OptionValue[]
                        : []
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectMultiValue(value)) {
                        setValues({
                          ...values,
                          authorIds: value.map((v) => v.value as string),
                        });
                      } else {
                        setValues({
                          ...values,
                          authorIds: [],
                        });
                      }
                    }}
                  />
                  {errors.authorIds && (
                    <p className="text-error-main text-sm">
                      {Array.isArray(errors.authorIds)
                        ? errors.authorIds.join(", ")
                        : errors.authorIds}
                    </p>
                  )}
                </div>
                <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                  <label className="text-sm text-text-title">Schedule *</label>
                  {values.schedule.map((date) => (
                    <div className="flex flex-col" key={date}>
                      <div className="flex items-center space-x-2">
                        <div className="w-1/2 flex items-center justify-between border border-divider p-2 rounded-lg">
                          <label className="text-sm">{format(new Date(date), "PPp")}</label>
                        </div>
                        <button
                          className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                          onClick={() => {
                            if (values.schedule.length > 1) {
                              setValues({
                                ...values,
                                schedule: values.schedule.filter(
                                  (d) => d !== date
                                ),
                              });
                            }
                          }}
                          disabled={values.schedule.length <= 1}
                          aria-label={
                            values.schedule.length <= 1
                              ? "At least one schedule date is required"
                              : "Remove this schedule date"
                          }
                        >
                          <MinusOutlined
                            className={`h-5 w-5 ${
                              values.schedule.length > 1
                                ? "text-error-main"
                                : "text-action-disabled"
                            }`}
                          />
                        </button>
                      </div>
                      <label className="text-xs text-text-title italic py-2">
                        and
                      </label>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2">
                    <div className="w-1/2 flex items-center space-x-2">
                      <DatePicker
                        defaultValue={getDefaultScheduleDate(values.schedule)}
                        readOnly={false}
                        onChanged={(date) => setNewScheduleDate(date)}
                      />
                      {newScheduleDate && (
                        <TimePicker
                          value={newScheduleTime}
                          onChange={setNewScheduleTime}
                          disabled={false}
                        />
                      )}
                    </div>
                    <button
                      className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                      onClick={() => {
                        if (newScheduleDate && newScheduleTime) {
                          const [hour, minute] = newScheduleTime.split(":");
                          if (!hour || !minute) return;
                          const isoDate = new Date(newScheduleDate);
                          isoDate.setHours(
                            parseInt(hour),
                            parseInt(minute),
                            0,
                            0
                          );
                          setValues({
                            ...values,
                            schedule: [
                              ...values.schedule,
                              isoDate.toISOString(),
                            ],
                          });
                          setNewScheduleDate(null);
                          setNewScheduleTime("");
                        }
                      }}
                      disabled={!(newScheduleDate && newScheduleTime)}
                      aria-label={
                        newScheduleDate && newScheduleTime
                          ? "Add schedule date"
                          : "Select a date and time to enable adding"
                      }
                    >
                      <AddOutlined
                        className={`w-5 h-5 ${
                          newScheduleDate && newScheduleTime
                            ? "text-success-main"
                            : "text-action-disabled"
                        }`}
                      />
                    </button>
                  </div>
                  {errors.schedule && (
                    <p className="text-error-main text-sm">
                      {errors.schedule as string}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4 pt-4">
              <Button
                label="Submit"
                type="submit"
                loading={createCourseState.loading}
                disabled={!dirty || isSubmitting || createCourseState.loading}
              />
            </div>
          </form>
        )}
      </Formik>
      <ToastComponent id="addCourse" />
    </PermissionGate>
  );
}